import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DayStatus } from "@sundial/core";
import { useMonth, useLogDay, useSettings, useClients } from "../api/queries.js";
import { SunTile } from "../components/SunTile.js";
import { ClientFilter } from "../components/ClientFilter.js";
import { DayEditor } from "../components/DayEditor.js";
import { Toast } from "../components/Toast.js";
import { isOffDay } from "../lib/offday.js";
import { MonthPicker } from "../components/MonthPicker.js";
import { ImportCard } from "./ImportCard.js";

/* ── Helpers (same as Dashboard) ─────────────────────────── */

function todayParis(): string {
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function thisMonth(): string {
  return todayParis().slice(0, 7);
}

function parseYM(m: string): [number, number] {
  const parts = m.split("-");
  return [parseInt(parts[0] ?? "2026", 10), parseInt(parts[1] ?? "1", 10)];
}

function prevMonth(m: string): string {
  const [y, mo] = parseYM(m);
  const d = new Date(y, mo - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function nextMonth(m: string): string {
  const [y, mo] = parseYM(m);
  const d = new Date(y, mo, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(m: string): string {
  const [y, mo] = parseYM(m);
  return new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(
    new Date(y, mo - 1, 1),
  );
}

function buildCalendarGrid(month: string): (string | null)[] {
  const [y, mo] = parseYM(month);
  const firstDay = new Date(y, mo - 1, 1);
  const daysInMonth = new Date(y, mo, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7;
  const cells: (string | null)[] = Array(startOffset).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${month}-${String(d).padStart(2, "0")}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function shortDateLabel(date: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(date + "T12:00:00"));
}

/* ── Calendar ─────────────────────────────────────────────── */

export function Calendar() {
  const { t } = useTranslation();
  const [month, setMonth] = useState<string>(thisMonth);
  const [showImport, setShowImport] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; onUndo?: () => void } | null>(null);
  const [clientId, setClientId] = useState("");

  const { data: monthData, isLoading } = useMonth(month);
  const logDay = useLogDay(month);
  const { data: settings } = useSettings();
  const { data: clientsData } = useClients();
  const frHolidaysEnabled = settings?.frHolidaysEnabled ?? true;

  const dayMap = new Map<string, DayStatus>();
  const noteMap = new Map<string, string>();
  const clientMap = new Map<string, string>();
  const allocMap = new Map<string, { clientId: string; status: "full" | "half" }[]>();
  if (monthData) {
    for (const d of monthData.days) {
      dayMap.set(d.date, d.status);
      if (d.note) noteMap.set(d.date, d.note);
      if (d.clientId) clientMap.set(d.date, d.clientId);
      if (d.allocations?.length) allocMap.set(d.date, d.allocations);
    }
  }

  // A day matches the client filter only if it is worked AND that client is on
  // it (primary or an extra). Non-matching days dim but stay editable.
  const matchesClient = (date: string): boolean => {
    if (!clientId) return true;
    const st = dayMap.get(date);
    if (st !== "full" && st !== "half") return false;
    return clientMap.get(date) === clientId || (allocMap.get(date) ?? []).some((a) => a.clientId === clientId);
  };
  const anyMatch = !clientId || [...dayMap.keys()].some((d) => d.startsWith(month) && matchesClient(d));

  const missed = monthData?.missed ?? [];
  const grid = buildCalendarGrid(month);
  const todayStr = todayParis();
  const isCurrentMonth = month === thisMonth();

  /* ── Backfill quick-log ──────────────────────────────────── */
  function quickLog(date: string, status: DayStatus) {
    const prevStatus = dayMap.get(date);
    logDay.mutate({ date, status });
    setToast({
      message: `${shortDateLabel(date)} set to ${status}`,
      onUndo: prevStatus
        ? () => {
            logDay.mutate({ date, status: prevStatus });
            setToast(null);
          }
        : undefined,
    });
  }

  /* ── DayEditor close with toast ─────────────────────────── */
  function handleEditorClose(savedDate?: string, savedStatus?: DayStatus, prevStatus?: DayStatus | null) {
    setSelectedDate(null);
    if (savedDate && savedStatus) {
      setToast({
        message: `${shortDateLabel(savedDate)} set to ${savedStatus}`,
        onUndo: prevStatus != null
          ? () => {
              logDay.mutate({ date: savedDate, status: prevStatus });
              setToast(null);
            }
          : undefined,
      });
    }
  }

  const selectedDayStatus = selectedDate ? (dayMap.get(selectedDate) ?? null) : null;

  return (
    <div className="page-content">
      {/* ── Month switcher (matches design: ‹ · centered label · › · This month) ── */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
        }}
      >
        <button
          className="btn secondary"
          style={{ width: "38px", height: "38px", padding: 0, flex: "none" }}
          onClick={() => setMonth(prevMonth)}
          aria-label="Previous month"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
        {/* Month label — click to open the picker. Same component as the
            Dashboard: jumping a year back to backfill should not mean
            clicking ‹ twelve times. */}
        <button
          onClick={() => setPickerOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
          style={{
            font: "700 18px var(--font-head)",
            letterSpacing: "-.01em",
            textAlign: "center",
            flex: 1,
            minWidth: 0,
            background: "none",
            border: "none",
            color: "var(--text)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          {monthLabel(month)}
          <span aria-hidden="true" style={{ fontSize: "10px" }}>▾</span>
        </button>
        <button
          className="btn secondary"
          style={{ width: "38px", height: "38px", padding: 0, flex: "none" }}
          onClick={() => setMonth(nextMonth)}
          aria-label="Next month"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
        <button
          className="btn secondary"
          style={{ height: "38px", padding: "0 12px", flex: "none", fontSize: "12px", whiteSpace: "nowrap" }}
          onClick={() => setMonth(thisMonth)}
          disabled={isCurrentMonth}
        >
          This month
        </button>
        {/* Days import lives here, beside the days it creates — not on Reports,
            which is a read/export screen. Same toggle pattern as Payments. */}
        <button
          className="btn secondary"
          style={{ height: "38px", padding: "0 12px", flex: "none", fontSize: "12px", whiteSpace: "nowrap" }}
          onClick={() => setShowImport((v) => !v)}
          aria-expanded={showImport}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ marginRight: 6, verticalAlign: -2 }}
          >
            <path d="M12 16V4M7 9l5-5 5 5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
          </svg>
          {t("importCard.trigger")}
        </button>
        {pickerOpen && (
          <MonthPicker value={month} onPick={setMonth} onClose={() => setPickerOpen(false)} />
        )}
      </div>

      {/* ── CSV import (inline) ──────────────────────────────── */}
      {showImport && <ImportCard only="days" title={t("importCard.titleDays")} />}

      {/* ── Backfill banner ──────────────────────────────────── */}
      {missed.length > 0 && (
        <div
          style={{
            background: "linear-gradient(135deg, rgba(244,122,32,.10), rgba(244,122,32,.03))",
            border: "1px solid color-mix(in srgb, var(--primary) 28%, var(--border))",
            borderRadius: "16px",
            padding: "16px 18px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "9px",
              font: "600 14px var(--font-head)",
              marginBottom: "4px",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 17h16M8 17a4 4 0 0 1 8 0M12 4v3M6.5 8.5l1.3 1.2M17.5 8.5l-1.3 1.2" />
            </svg>
            Fill in earlier days
          </div>
          <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "12px" }}>
            A couple of days are still open. Set them whenever — no rush.
          </div>
          {missed.map((date) => (
            <div
              key={date}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "9px 0",
                borderTop: "1px solid var(--border)",
              }}
            >
              <span style={{ flex: 1, fontSize: "13.5px", fontWeight: 500 }}>
                {shortDateLabel(date)}
              </span>
              <button
                onClick={() => quickLog(date, "full")}
                style={{
                  height: "34px",
                  padding: "0 12px",
                  border: "1px solid var(--border)",
                  borderRadius: "9px",
                  background: "var(--card)",
                  color: "var(--text)",
                  font: "600 12px var(--font-head)",
                  cursor: "pointer",
                }}
              >
                Full
              </button>
              <button
                onClick={() => quickLog(date, "half")}
                style={{
                  height: "34px",
                  padding: "0 12px",
                  border: "1px solid var(--border)",
                  borderRadius: "9px",
                  background: "var(--card)",
                  color: "var(--text)",
                  font: "600 12px var(--font-head)",
                  cursor: "pointer",
                }}
              >
                Half
              </button>
              <button
                onClick={() => quickLog(date, "none")}
                style={{
                  height: "34px",
                  padding: "0 12px",
                  border: "1px solid var(--border)",
                  borderRadius: "9px",
                  background: "var(--card)",
                  color: "var(--muted)",
                  font: "600 12px var(--font-head)",
                  cursor: "pointer",
                }}
              >
                None
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Client filter (hidden at a single client) ────────── */}
      <div style={{ marginBottom: 10 }}>
        <ClientFilter value={clientId} onChange={setClientId} />
      </div>

      {/* ── Calendar grid ────────────────────────────────────── */}
      <div
        className="card"
        style={{ padding: "18px" }}
      >
        {isLoading && (
          <div style={{ textAlign: "center", color: "var(--muted)", fontSize: "13px", padding: "24px 0" }}>
            Loading…
          </div>
        )}
        {clientId && !anyMatch && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 13, padding: "9px 12px", borderRadius: 10, background: "var(--surface)" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--muted2)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}>
              <rect x="3" y="4.5" width="18" height="16" rx="3" />
              <path d="M3 9h18M8 2.5v4M16 2.5v4" />
            </svg>
            <span style={{ fontSize: 12.5, color: "var(--muted)", fontWeight: 500 }}>
              {t("clientFilter.noDays", { name: clientsData?.clients.find((c) => c.id === clientId)?.name ?? "" })}
            </span>
          </div>
        )}
        <div
          role="grid"
          aria-label={`Editable calendar for ${monthLabel(month)}`}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "6px",
          }}
        >
          {/* Day-of-week headers */}
          {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
            <div
              key={d}
              style={{
                textAlign: "center",
                font: "600 10px var(--font-head)",
                letterSpacing: ".04em",
                color: "var(--muted2)",
                paddingBottom: "2px",
              }}
            >
              {d}
            </div>
          ))}

          {/* Day tiles */}
          {grid.map((date, i) => {
            if (!date) {
              return <div key={`empty-${i}`} />;
            }
            const loggedStatus = dayMap.get(date);
            const status = loggedStatus ?? (isOffDay(date, frHolidaysEnabled) ? "none" : undefined);
            const isToday = date === todayStr;
            const billable = loggedStatus === "full" || loggedStatus === "half";
            const clients = billable ? 1 + (allocMap.get(date)?.length ?? 0) : 0;
            return (
              <SunTile
                key={date}
                date={date}
                status={status}
                today={isToday}
                hasNote={Boolean(noteMap.get(date))}
                clients={clients}
                dimmed={clientId ? !matchesClient(date) : false}
                onClick={() => setSelectedDate(date)}
              />
            );
          })}
        </div>
      </div>

      {/* Coming soon note */}
      <div
        style={{
          marginTop: "14px",
          padding: "13px 15px",
          borderRadius: "12px",
          border: "1px dashed var(--border)",
          background: "var(--surface)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          color: "var(--muted2)",
          fontSize: "12.5px",
        }}
      >
        <span
          style={{
            font: "600 9px var(--font-head)",
            letterSpacing: ".05em",
            textTransform: "uppercase",
            background: "var(--card)",
            border: "1px solid var(--border)",
            color: "var(--muted2)",
            padding: "2px 7px",
            borderRadius: "6px",
            flexShrink: 0,
          }}
        >
          Soon
        </span>
        Multi-day range (vacation block), per-client tag, note attachments
      </div>

      {/* ── Day Editor overlay ───────────────────────────────── */}
      {selectedDate && (
        <DayEditor
          date={selectedDate}
          current={selectedDayStatus}
          month={month}
          currentNote={selectedDate ? (noteMap.get(selectedDate) ?? "") : ""}
          currentClientId={selectedDate ? (clientMap.get(selectedDate) ?? null) : null}
          currentAllocations={selectedDate ? (allocMap.get(selectedDate) ?? []) : []}
          onClose={handleEditorClose}
        />
      )}

      {/* ── Toast ────────────────────────────────────────────── */}
      {toast && (
        <Toast
          message={toast.message}
          onUndo={toast.onUndo}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
